import type { PartnerKonto, PartnerRecht, PartnerRolle, PartnerRollenrecht } from '@nac2s/shared'
import { partnerRechte } from '@nac2s/shared'
import wasserzeichen from '@nac2s/ui/marke/veeky-schriftzug.png'
import { useCallback, useEffect, useId, useState } from 'react'
import { Dialog, Modal, ModalOverlay } from '@/components/application/modals/modal'
import { Select } from '@/components/base/select/select'
import { Knopf } from '@/teile/Knopf'
import { Merkmalsliste } from '@/teile/Merkmalsliste'
import { Status } from '@/teile/Status'
import { Fehler, Leer, Tabelle } from '@/teile/Tabelle'
import { Zahl } from '@/teile/Zahl'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { kennwortZuruecksetzen, kontoAendern, kontoAnlegen, ladeKonten } from '../api/partner.js'

/**
 * Konten der Kundenverwaltung.
 *
 * **netable legt genau eines an, das erste.** Alles Weitere macht der Partner
 * selbst (`Projektstruktur.md` §3.0) — wer bei jedem neuen Mitarbeiter beim
 * Hersteller anfragen muss, hat keinen Betrieb, sondern ein Ticketsystem.
 *
 * Was ein Konto darf, entscheidet **seine Rolle**, nicht ein Schalter je
 * Recht. Die Seite zeigt die Rolle als eigene Spalte und nicht versteckt in
 * einem Untermenü: Es ist die folgenreichste Angabe an einem Konto, und sie
 * soll beim Draufsehen ablesbar sein.
 *
 * Zwei Dinge setzt der Server durch, nicht diese Seite — hier stehen sie nur
 * als Hinweis: Es gibt keine Selbstregistrierung, und das letzte Konto mit
 * Verwaltungsrecht lässt sich weder sperren noch entrechten. Eine Oberfläche,
 * die eine Regel allein trägt, trägt sie nicht.
 */

/**
 * Die Rollen kommen vom Server — Schlüssel, Name und Rechte
 * (`partnerKontenSchema.rollen`). Rollen sind Daten (SECURITY.md §4); hier
 * steht keine Liste mehr, die mit der Registry auseinanderlaufen könnte.
 *
 * In der Auswahl steht **nur der Name**: In einer Tabellenzeile ist das Feld
 * anderthalb Zentimeter breit, ein abgeschnittener Halbsatz erklärt nichts.
 * Was eine Rolle mitbringt, steht beim Anlegen im Modal.
 */
function rollenwahl(rollen: PartnerRollenrecht[]) {
  return rollen.map((r) => ({ id: r.rolle, label: r.name }))
}

/** Die Vorgabe beim Anlegen: die erste Rolle ohne Kontenverwaltung, sonst die erste. */
function vorgaberolle(rollen: PartnerRollenrecht[]): PartnerRolle {
  return (
    rollen.find((r) => r.rolle === 'technik')?.rolle ??
    rollen.find((r) => !r.rechte.includes('konten.verwalten'))?.rolle ??
    rollen[0]?.rolle ??
    ''
  )
}

/** Konten, deren Rolle Konten verwalten darf — am Recht gezählt, nicht am Namen. */
function verwaltet(rolle: string, rollen: PartnerRollenrecht[]): boolean {
  return rollen.find((r) => r.rolle === rolle)?.rechte.includes('konten.verwalten') ?? false
}

/**
 * Die Rechte in Worten.
 *
 * Die Zuordnung Rolle → Rechte kommt vom Server (`rollen.ts`); **wie** ein
 * Recht heißt, ist Sache der Oberfläche. Deshalb steht hier Text und keine
 * Regel: Ginge beides hier, liefe die Anzeige irgendwann neben der Prüfung
 * her, ohne dass es jemand bemerkt.
 */
const RECHTWORTE: Record<PartnerRecht, string> = {
  'kunden.sehen': 'Kunden, Anwender und Geräte ansehen',
  'kunden.technik': 'Anwender anlegen, Kopplungscodes, Geräte je Anwender',
  'kunden.kaufmaennisch': 'Seeds-Obergrenze und Edition ändern',
  'kunden.anlegen': 'Neue Kunden beantragen und abschalten',
  'konten.verwalten': 'Konten anlegen, sperren, Kennwörter setzen',
  'partner.profil': 'Unternehmens- und Supportdaten pflegen',
}

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; konten: PartnerKonto[]; rollen: PartnerRollenrecht[] }
  | { status: 'fehler'; meldung: string }

/** Ein gerade erzeugtes Kennwort — einmalig sichtbar, bis es weggeklickt wird. */
interface Kennwortzettel {
  email: string
  kennwort: string
  neu: boolean
}

export function Konten() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [formularOffen, setFormularOffen] = useState(false)
  const [zettel, setZettel] = useState<Kennwortzettel | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [arbeitetAn, setArbeitetAn] = useState<string | null>(null)

  const holen = useCallback(async () => {
    try {
      const antwort = await ladeKonten()
      setZustand({ status: 'da', konten: antwort.konten, rollen: antwort.rollen })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    void holen()
  }, [holen])

  /**
   * Führt eine Änderung aus und lädt die Liste neu.
   *
   * Die Liste wird geholt statt im Browser fortgeschrieben: Der Server ist die
   * Wahrheit, und er lehnt manches ab — eine Anzeige, die die Änderung schon
   * zeigt und danach zurückspringt, ist schlimmer als eine, die kurz wartet.
   */
  async function aendere(id: string, tun: () => Promise<void>) {
    setMeldung(null)
    setArbeitetAn(id)
    try {
      await tun()
      await holen()
    } catch (fehler: unknown) {
      setMeldung(satzZu(fehler))
    } finally {
      setArbeitetAn(null)
    }
  }

  if (zustand.status === 'laedt') {
    return (
      <p role="status" className="px-6 py-10 text-sm text-tertiary">
        Konten werden geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler') {
    return (
      <div className="px-6 py-10">
        <Fehler>
          <span className="font-medium">Die Konten konnten nicht geladen werden</span>
          <br />
          {zustand.meldung}
        </Fehler>
      </div>
    )
  }

  const { konten, rollen } = zustand
  const admins = konten.filter((k) => verwaltet(k.rolle, rollen) && !k.gesperrt)
  const gesperrte = konten.filter((k) => k.gesperrt)

  return (
    <div>
      {/* Derselbe Kopf wie die Kundenliste: Schimmer aus der Ecke, Marke als
          Wasserzeichen dahinter, eine grosse Zahl. Eine zweite Bauform fuer
          dieselbe Art Seite waere nur anders, nicht besser. */}
      <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
        <div
          aria-hidden="true"
          className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
        />
        <img
          src={wasserzeichen}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 hidden w-[20rem] -translate-x-1/2 -translate-y-1/2 opacity-[0.08] select-none lg:block"
        />
        <div className="relative">
          <p className="text-[13px] text-quaternary">Zugang zur Kundenverwaltung</p>
          <div className="mt-7 flex flex-wrap items-end gap-6">
            <Zahl
              wert={konten.length}
              className="font-display text-[3.8rem] leading-[0.86] font-extrabold tracking-[-0.035em] text-primary"
            />
            <div className="pb-1">
              <p className="font-semibold text-primary">
                {konten.length === 1 ? 'Konto' : 'Konten'}
              </p>
              <p className="text-sm text-tertiary">nur für Ihr Haus</p>
            </div>
            <dl className="ml-auto flex flex-wrap gap-6 pb-1">
              <Kennzahl name="Admins" wert={admins.length} />
              <Kennzahl name="Gesperrt" wert={gesperrte.length} achtung={gesperrte.length > 0} />
            </dl>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-6 pb-3.5">
        <p className="text-[13px] text-tertiary">
          Die Rolle entscheidet, was ein Konto ändern darf — nicht, was es sieht.
        </p>
        <div className="ml-auto">
          <Knopf stark onClick={() => setFormularOffen(true)}>
            Konto anlegen
          </Knopf>
        </div>
      </div>

      {formularOffen ? (
        <AnlegenModal
          rollen={rollen}
          onAbbruch={() => setFormularOffen(false)}
          onFertig={async (kennwort, email) => {
            setFormularOffen(false)
            setZettel({ email, kennwort, neu: true })
            await holen()
          }}
          onFehler={(text) => {
            setFormularOffen(false)
            setMeldung(text)
          }}
        />
      ) : null}

      {zettel ? <Zettel zettel={zettel} onZu={() => setZettel(null)} /> : null}

      {meldung ? (
        <div className="px-6 pb-3.5">
          <Fehler>{meldung}</Fehler>
        </div>
      ) : null}

      <div className="px-6 pb-8">
        {konten.length === 0 ? (
          <Leer titel="Noch kein Konto">
            Das erste Konto legt netable an. Danach verwalten Sie sie hier selbst.
          </Leer>
        ) : (
          <Tabelle kopf={['Konto', 'Rolle', 'Zustand', 'Zuletzt gesehen', '']}>
            {konten.map((konto) => (
              <Zeile
                key={konto.id}
                konto={konto}
                rollen={rollen}
                beschaeftigt={arbeitetAn === konto.id}
                onRolle={(rolle) => aendere(konto.id, () => kontoAendern(konto.id, { rolle }))}
                onSperre={(gesperrt) =>
                  aendere(konto.id, () => kontoAendern(konto.id, { gesperrt }))
                }
                onKennwort={() =>
                  aendere(konto.id, async () => {
                    const neu = await kennwortZuruecksetzen(konto.id)
                    setZettel({ email: konto.email, kennwort: neu.kennwort, neu: false })
                  })
                }
              />
            ))}
          </Tabelle>
        )}
      </div>
    </div>
  )
}

function Zeile({
  konto,
  rollen,
  beschaeftigt,
  onRolle,
  onSperre,
  onKennwort,
}: {
  konto: PartnerKonto
  rollen: PartnerRollenrecht[]
  beschaeftigt: boolean
  onRolle: (rolle: PartnerRolle) => void
  onSperre: (gesperrt: boolean) => void
  onKennwort: () => void
}) {
  return (
    <tr className={cx('border-b border-secondary last:border-0', konto.gesperrt && 'opacity-60')}>
      <td className="py-[11px] pr-2.5">
        <span className="font-medium text-primary">{konto.anzeigename}</span>
        {konto.ichSelbst ? (
          <span className="ml-2 rounded-md bg-secondary px-1.5 py-px text-[11px] text-tertiary">
            Sie
          </span>
        ) : null}
        <br />
        <span className="text-[12.5px] text-quaternary">{konto.email}</span>
      </td>
      <td className="py-[11px] pr-2.5">
        <Rollenwahl
          rollen={rollen}
          wert={konto.rolle}
          disabled={beschaeftigt}
          beschriftung={`Rolle von ${konto.anzeigename}`}
          onWahl={onRolle}
        />
      </td>
      <td className="py-[11px] pr-2.5">
        <Status ton={konto.gesperrt ? 'ruhig' : 'gut'}>
          {konto.gesperrt ? 'gesperrt' : 'aktiv'}
        </Status>
      </td>
      <td className="py-[11px] pr-2.5 text-tertiary tabular-nums">
        {konto.zuletztGesehen ? datum(konto.zuletztGesehen) : '—'}
      </td>
      <td className="py-[11px]">
        <div className="flex flex-wrap justify-end gap-2">
          <Knopf disabled={beschaeftigt} onClick={onKennwort}>
            Kennwort
          </Knopf>
          {konto.gesperrt ? (
            <Knopf disabled={beschaeftigt} onClick={() => onSperre(false)}>
              Freigeben
            </Knopf>
          ) : (
            <Knopf gefahr disabled={beschaeftigt} onClick={() => onSperre(true)}>
              Sperren
            </Knopf>
          )}
        </div>
      </td>
    </tr>
  )
}

/**
 * Auswahl der Rolle.
 *
 * Eine Auswahl und kein Schalter: Es sind drei Werte, nicht zwei, und sie sind
 * keine Steigerung voneinander — Technik und Vertrieb dürfen verschiedene
 * Dinge, nicht mehr oder weniger davon.
 *
 * Die Beschriftung ist für Vorleseprogramme da und sonst verborgen: Die
 * Spaltenüberschrift sagt Sehenden schon, worum es geht, und sie in jeder
 * Zeile zu wiederholen machte die Spalte breit ohne Gewinn.
 */
function Rollenwahl({
  rollen,
  wert,
  disabled,
  beschriftung,
  label,
  onWahl,
}: {
  rollen: PartnerRollenrecht[]
  wert: PartnerRolle
  disabled?: boolean
  beschriftung: string
  /** Sichtbare Beschriftung. Ohne sie traegt nur `beschriftung` fuer Vorleser. */
  label?: string
  onWahl: (rolle: PartnerRolle) => void
}) {
  return (
    <Select
      size="sm"
      aria-label={beschriftung}
      {...(label ? { label } : {})}
      items={rollenwahl(rollen)}
      selectedKey={wert}
      isDisabled={disabled ?? false}
      onSelectionChange={(schluessel) => onWahl(schluessel as PartnerRolle)}
      className={label ? undefined : 'w-[8.5rem]'}
    >
      {(eintrag) => <Select.Item {...eintrag} />}
    </Select>
  )
}

/**
 * Konto anlegen — im Modal, nicht als Streifen über der Tabelle.
 *
 * Der Grund ist die Rolle. Sie nebenher in ein schmales Feld zu legen hiesse,
 * die folgenreichste Angabe am Konto so zu stellen, dass niemand lesen kann,
 * was sie bedeutet. Im Modal stehen die Felder untereinander, und daneben
 * steht, was die gewählte Rolle mitbringt — und was sie nicht mitbringt.
 */
function AnlegenModal({
  rollen,
  onFertig,
  onAbbruch,
  onFehler,
}: {
  rollen: PartnerRollenrecht[]
  onFertig: (kennwort: string, email: string) => Promise<void>
  onAbbruch: () => void
  onFehler: (meldung: string) => void
}) {
  const [email, setEmail] = useState('')
  const [anzeigename, setAnzeigename] = useState('')
  // Vorgabe: die engere Arbeitsrolle. Wer ein Konto anlegt und nicht ueber
  // die Rolle nachdenkt, soll nicht versehentlich Zugang zur
  // Kontenverwaltung vergeben.
  const [rolle, setRolle] = useState<PartnerRolle>(() => vorgaberolle(rollen))
  const [laeuft, setLaeuft] = useState(false)
  const titelId = useId()

  const vollstaendig = email.trim().length > 3 && anzeigename.trim().length > 0

  return (
    <ModalOverlay
      isOpen
      isDismissable={!laeuft}
      onOpenChange={(offen) => {
        if (!offen) onAbbruch()
      }}
    >
      <Modal className="w-full max-w-[46rem]">
        <Dialog aria-labelledby={titelId}>
          <form
            className="flex flex-col gap-5 p-6"
            onSubmit={(e) => {
              e.preventDefault()
              if (!vollstaendig || laeuft) return
              setLaeuft(true)
              kontoAnlegen({ email: email.trim(), anzeigename: anzeigename.trim(), rolle })
                .then((antwort) => onFertig(antwort.kennwort, email.trim()))
                .catch((fehler: unknown) => onFehler(satzZu(fehler)))
                .finally(() => setLaeuft(false))
            }}
          >
            <div>
              <h2 id={titelId} className="font-display text-lg font-bold text-primary">
                Konto anlegen
              </h2>
              <p className="mt-1 text-[13px] text-tertiary">
                Das Kennwort erscheint <strong>einmalig</strong>, sobald das Konto steht. Danach
                lässt es sich nur neu erzeugen.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-[1fr_17rem]">
              {/* Untereinander, jedes ueber die volle Spaltenbreite: Die
                  Auswahl klappt so breit auf, wie sie geschlossen ist —
                  daran hing, dass man die Rollen nicht lesen konnte. */}
              <div className="flex flex-col gap-4">
                <Feld
                  name="Name"
                  wert={anzeigename}
                  onWert={setAnzeigename}
                  platzhalter="Vor- und Nachname"
                  autoFocus
                />
                <Feld
                  name="E-Mail"
                  wert={email}
                  onWert={setEmail}
                  platzhalter="name@ihrhaus.de"
                  art="email"
                />
                <Rollenwahl
                  rollen={rollen}
                  wert={rolle}
                  beschriftung="Rolle des neuen Kontos"
                  label="Rolle"
                  onWahl={setRolle}
                />
              </div>

              <Rechteliste rollen={rollen} rolle={rolle} />
            </div>

            <div className="flex items-center justify-end gap-2.5 border-t border-secondary pt-4">
              <Knopf onClick={onAbbruch} disabled={laeuft}>
                Abbrechen
              </Knopf>
              <Knopf type="submit" stark disabled={!vollstaendig || laeuft}>
                {laeuft ? 'Wird angelegt …' : 'Anlegen'}
              </Knopf>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}

/**
 * Was die gewählte Rolle darf — und was nicht.
 *
 * Dieselbe Liste wie neben der Edition (`teile/Merkmalsliste.tsx`). Zwei
 * Fassungen liefen beim naechsten Mass auseinander, und dann stuende auf zwei
 * Seiten dasselbe verschieden da.
 *
 * Kennt der Server eine Rolle nicht, die hier in der Auswahl steht, bleibt die
 * Liste leer statt zu raten — dieselbe Antwort, die `darf()` gibt.
 */
function Rechteliste({ rollen, rolle }: { rollen: PartnerRollenrecht[]; rolle: PartnerRolle }) {
  const rechte = rollen.find((r) => r.rolle === rolle)?.rechte ?? []

  return (
    <Merkmalsliste
      titel="Was diese Rolle darf"
      merkmale={partnerRechte.map((recht) => ({
        name: RECHTWORTE[recht],
        enthalten: rechte.includes(recht),
      }))}
    />
  )
}

function Feld({
  name,
  wert,
  onWert,
  platzhalter,
  art = 'text',
  autoFocus,
}: {
  name: string
  wert: string
  onWert: (wert: string) => void
  platzhalter: string
  art?: 'text' | 'email'
  autoFocus?: boolean
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11.5px] font-medium text-quaternary">{name}</span>
      <input
        type={art}
        value={wert}
        placeholder={platzhalter}
        // biome-ignore lint/a11y/noAutofocus: Im Modal ist das erste Feld das Ziel — der Fokus muss ohnehin hinein, sonst liest ein Vorleser den Dialog gar nicht erst vor.
        autoFocus={autoFocus ?? false}
        onChange={(e) => onWert(e.target.value)}
        className="feld min-h-[40px] rounded-[10px] bg-primary px-3 text-[13.5px] text-primary transition-[box-shadow] duration-[180ms] ease-out outline-none focus:feld-schein"
      />
    </label>
  )
}

/**
 * Das Kennwort, einmalig.
 *
 * Bleibt stehen, bis es weggeklickt wird — nicht als Einblendung, die nach
 * Sekunden verschwindet. Wer es in diesem Moment nicht weitergeben kann, hat
 * sonst keinen zweiten Versuch und muss ein neues erzeugen.
 */
function Zettel({ zettel, onZu }: { zettel: Kennwortzettel; onZu: () => void }) {
  const [kopiert, setKopiert] = useState(false)

  return (
    <div className="mx-6 mb-4 rounded-xl border border-achtung bg-achtung-flaeche p-4">
      <p className="text-[13px] font-semibold text-achtung-tief">
        {zettel.neu ? 'Konto angelegt' : 'Kennwort zurückgesetzt'} — {zettel.email}
      </p>
      <p className="mt-1 text-[12.5px] text-achtung-tief">
        Dieses Kennwort erscheint <strong>nur jetzt</strong>. Es lässt sich später nicht mehr
        abrufen, nur neu erzeugen.
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
        <code className="rounded-lg border border-secondary bg-primary px-3 py-2 font-mono text-[13px] text-primary select-all">
          {zettel.kennwort}
        </code>
        <Knopf
          onClick={() => {
            void navigator.clipboard?.writeText(zettel.kennwort).then(() => setKopiert(true))
          }}
        >
          {kopiert ? 'Kopiert' : 'Kopieren'}
        </Knopf>
        <Knopf onClick={onZu}>Schließen</Knopf>
      </div>
    </div>
  )
}

function Kennzahl({ name, wert, achtung }: { name: string; wert: number; achtung?: boolean }) {
  return (
    <div>
      <dt className="text-[11.5px] text-quaternary">{name}</dt>
      <dd
        className={cx(
          'font-display text-lg font-bold tabular-nums',
          achtung ? 'text-achtung-tief' : 'text-primary',
        )}
      >
        {wert}
      </dd>
    </div>
  )
}

/**
 * Macht aus einem Fehler einen Satz, den jemand lesen kann.
 *
 * Die beiden Fälle, die der Server ausdrücklich kennt, bekommen einen eigenen
 * Satz — „Der Vorgang widerspricht dem aktuellen Zustand" hilft niemandem
 * weiter, der wissen will, was er stattdessen tun soll.
 */
function satzZu(fehler: unknown): string {
  if (fehler instanceof ApiError) {
    if (fehler.code === 'email_belegt') return 'Diese E-Mail-Adresse gehört bereits zu einem Konto.'
    if (fehler.code === 'letzter_verwalter') {
      return 'Das geht nicht: Es wäre das letzte Konto, das Konten verwalten darf. Geben Sie zuerst einem anderen Konto dieses Recht.'
    }
    return fehler.message
  }
  return 'Unbekannter Fehler'
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
